const breakpoints = {
  xs: 375,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1170,
  xxl: 1280
} as const;

const COMMUNITY_MAX_MESSAGE_LENGTH = 700;
const COMMUNITY_MAX_IMAGES = 10;

const smoothCenterViewScrollOptions: ScrollIntoViewOptions = {
  block: 'center',
  behavior: 'smooth'
};

const campaignType = {
  HOBBII: 'hobbii'
};

const userCookies = {
  C_ID: 'c_id',
  CONTACT_UUID: 'contact_uuid',
  CURRENCY_RATE: 'currency_rate',
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  CONSENT_STATUS: 'consent_status',
  REWARDS_VISIBILITY_ALREADY_TRACKED: 'rewards_visibility_already_tracked',
  CAPTURED_PROFILE: 'captured_profile',
  // The person key Cognito App mints, written from the enrichment response.
  // Named to match the field on the internal event bus and the
  // `hobbii.cognito_uuid` Shopify metafield, so one value does not collect a
  // different name on every hop. The Shopify app extension writes the same
  // name, so this is a cross-surface contract, not a theme-local detail.
  HOBBII_COGNITO_UUID: 'hobbii_cognito_uuid',
  DOMAIN_MISMATCH_SUPPRESSED: 'domain_mismatch_suppressed'
};

/**
 * How long the wrong-domain nudge stays suppressed on a given host. Accepting
 * is not a preference to stay on the wrong domain, so its window only has to
 * cover the navigation away and a quick bounce back - it must expire long
 * before a later, deliberate return, which should be nudged again.
 */
const domainMismatchNudge = {
  DISMISSED_DAYS: 30,
  ACCEPTED_HOURS: 1
} as const;

/**
 * Legacy cart attribute; removed on first getCart when present. Do not set.
 */
const LEGACY_CART_ATTRIBUTE_AOV_AWARD_SHOWN = 'aov_award_shown';

const clientCookies = {
  PRODUCT_ANALYTICS_CLIENT_ID: 'pa_client_id',
  PRODUCT_ANALYTICS_USER_ID: 'pa_user_id',
  POSTHOG_API_KEY: 'posthog_api_key',
  POSTHOG_API_HOST: 'posthog_api_host'
};

const modalEvents = {
  open: 'hb-modal:open',
  close: 'hb-modal:close'
};

const searchEvents = {
  NATIVE_HEADER_SWAPPED: 'hobbii:native-search-header-swapped'
};

const crossSellConfig = {
  MIN_PRODUCTS: 5,
  MAX_ADDITIONAL_PRODUCTS: 5,
  INIT_DELAY_MS: 100,
  FADE_ANIMATION_DURATION_MS: 400
} as const;

const posthogEvents = {
  PRODUCT_VIEWED: 'product_viewed',
  VARIANT_SELECTED: 'variant_selected',
  PATTERN_PRODUCTS_ADD_TO_CART: 'pattern_products_add_to_cart',
  GIFT_PICKER_SHOWN: 'gift_picker_shown',
  GIFT_SELECTED: 'gift_selected',
  GIFT_REMOVED: 'gift_removed',
  REVIEW_ADDED: 'review_added',
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  FILTER_APPLIED: 'filter_applied',
  SEARCH_USED: 'search_used',
  SEARCH_FALLBACK: 'search_fallback',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',
  PATTERN_DOWNLOAD: 'pattern_download',
  PATTERN_DOWNLOAD_ANONYMOUS: 'pattern_download_anonymous',
  PATTERN_DOWNLOAD_RETRY: 'pattern_download_retry',
  // Shared across every UserDetailsModal/ProfileDetailsFields consumer
  // (pattern download, complete-profile nudge, post-signup, ...) —
  // distinguish them with the `form_source` property, not the event name.
  USER_DETAILS_FORM_SHOWN: 'user_details_form_shown',
  USER_DETAILS_FORM_SUBMITTED: 'user_details_form_submitted',
  USER_DETAILS_FORM_FIELD_FILLED: 'user_details_form_field_filled',
  OFFER_COUNTDOWN_SHOWN: 'offer_countdown_shown',
  ORDER_AMOUNT_DISCOUNT_VISUALIZER_MOUNTED:
    'order_amount_discount_visualizer_mounted',
  ORDER_AMOUNT_DISCOUNT_VISUALIZER_UPDATED:
    'order_amount_discount_visualizer_updated',
  CART_CROSS_SELL_PRODUCT_ADDED: 'cart_cross_sell_product_added_to_cart',
  CART_CROSS_SELL_SHOWN: 'cart_cross_sell_shown',
  NAVIGATION_CATEGORY_CLICKED: 'navigation_category_clicked',
  TOP_BANNER_CLICKED: 'top_banner_clicked',
  MERCHANDISING_BOX_CLICKED: 'merchandising_box_clicked',
  SECTION_VIEWED: 'section_viewed',
  SECTION_CLICKED: 'section_clicked',
  ADD_TO_CART: 'add_to_cart',
  ADD_TO_CART_CLICKED: 'add_to_cart_clicked',
  ADD_TO_CART_FAILED: 'add_to_cart_failed',
  CART_UPDATE_FAILED: 'cart_update_failed',
  DOMAIN_MISMATCH_NUDGE_SHOWN: 'domain_mismatch_nudge_shown',
  DOMAIN_MISMATCH_NUDGE_ACCEPTED: 'domain_mismatch_nudge_accepted',
  DOMAIN_MISMATCH_NUDGE_DISMISSED: 'domain_mismatch_nudge_dismissed',
  DOMAIN_MISMATCH_DETECTION_FAILED: 'domain_mismatch_detection_failed',
  JOINED_BINGO: 'joined_bingo',
  BINGO_LOBBY_VIEWED: 'bingo_lobby_viewed',
  BINGO_GAME_COMPLETED: 'bingo_game_completed',
  BINGO_WON: 'bingo_won',
  BINGO_PRIZE_WON: 'bingo_prize_won',
  BINGO_SESSION_ENDED: 'bingo_session_ended',
  BINGO_OUTRO_CLICKED: 'bingo_outro_clicked',
  BINGO_VIDEO_FAILED: 'bingo_video_failed',
  BINGO_VIDEO_RECOVERED: 'bingo_video_recovered',
  RECOMMENDATION_SLOT_VIEWED: 'recommendation_slot_viewed'
};

// The `form_source` property on the user_details_form_* events above —
// every UserDetailsModal/ProfileDetailsFields consumer gets its own entry
// here rather than minting its own event names for the same concern.
const userDetailsFormSources = {
  PATTERN_DOWNLOAD: 'pattern_download',
  COMPLETE_PROFILE: 'complete_profile',
  POST_SIGNUP: 'post_signup'
};

const bingoVideoRecoverySources = {
  PLAYBACK_CHANGE: 'playback_change',
  RECOVERED_EVENT: 'recovered_event',
  PLAYING_EDGE: 'playing_edge',
  ONLINE: 'online',
  PHASE_CHANGE: 'phase_change'
} as const;

const addToCartFailureReasons = {
  MISSING_VARIANT: 'missing_variant',
  VALIDATION: 'validation',
  REQUEST: 'request'
} as const;

const addToCartClickSources = {
  MAIN: 'main',
  STICKY: 'sticky'
} as const;

const addToCartClickOutcomes = {
  ADDED: 'added',
  OPTIONS_MODAL_OPENED: 'options_modal_opened'
} as const;

const cartUpdateFailureReasons = {
  VALIDATION: 'validation',
  UPDATE: 'update'
} as const;

const shopifyPixelEvents = {
  KIT_ADDED_TO_CART: 'hobbii:kit_added_to_cart'
};

const posthogFeatureFlags = {
  SHOW_CART_CROSS_SELL: 'show_cart_cross_sell',
  PATTERNS_LANDING_TEST: 'patterns_landing_test',
  RECO_PDP_ALSO_BOUGHT: 'reco_pdp_also_bought',
  RECO_HOME_PICKED_FOR_YOU: 'reco_home_picked_for_you',
  STICKY_ATC_OFFSCREEN: 'sticky_atc_offscreen'
};

const postSignupDetailsModal = {
  // Market/locale prefix aside -- see getAccountBase() in accountUrls.ts for
  // why that prefix (window.Shopify.routes.root) can't be hardcoded here.
  BINGO_PATH_SEGMENT: 'pages/bingo',
  DISMISSED_SESSION_KEY: 'post_signup_details_modal_dismissed'
};

const patternsLandingTestVariants = {
  CONTROL: 'control',
  TEST: 'test'
};

// Storage key for feature flags in session storage
const FEATURE_FLAGS_KEY = 'hobbii_feature_flags';

// Separate from FEATURE_FLAGS_KEY - a QA override is a manual, explicit
// choice that must apply without analytics consent, unlike a real
// PostHog-sourced flag value, so the two can't share storage.
const PATTERNS_LANDING_TEST_QA_OVERRIDE_KEY =
  'patterns_landing_test_qa_override';

// Reference currency used for cross-currency reporting (matches the custom pixel setting)
const REFERENCE_CURRENCY = 'dkk';

// product constants
const LOW_STOCK_THRESHOLD = 10;
const REVIEWS_PER_PAGE = 10;
/**
 * Page size for fetching the gift collection. The availability check and the gift
 * picker must share this window, or a purchasable gift beyond the smaller window drifts.
 */
const GIFT_COLLECTION_FETCH_WINDOW = 15;
const entityTypes: Record<string, string> = {
  PRODUCT: 'product',
  PATTERN: 'pattern',
  ARTICLE: 'article',
  KIT: 'kit'
};

const queryParams = {
  PATTERNS_LANDING_TEST_VARIANT: 'plt',
  SEARCH_QUERY: 'q',
  RELEWISE_SEARCH_QUERY: 'rw-term',
  SEARCH_VIEW: 'view',
  RELEWISE_FALLBACK_REASON: 'rw-fallback',
  DEBUG_TRACKING: 'debug_tracking'
};

const searchPage = {
  PATH_SEGMENT: 'search',
  NATIVE_VIEW: 'native'
} as const;

const productPageSelectors = {
  CART_BUTTON_BLOCK: '#js-product-cart-button-block'
} as const;

// Debounce (ms) for predictive search. Shared so the native predictive search
// (Autocomplete.vue's suggest fetch) and the Relewise predictive overlay's
// search_used emission (CE-438) settle on the SAME interval and behave
// identically - a multi-keystroke term fires search_used once, not per prefix.
const PREDICTIVE_SEARCH_DEBOUNCE_MS = 500;

const relewiseFallback = {
  TIMEOUT_MS: 5000,
  COOLDOWN_MS: 5 * 60 * 1000,
  PROBE_TIMEOUT_MS: 3000,
  STORAGE_KEY: 'relewiseFallback',
  PROBE_PATH: '/robots.txt',
  API_HOST: 'api.relewise.com',
  API_HOST_SUFFIX: '-api.relewise.com',
  API_SUBDOMAIN_SUFFIX: '.api.relewise.com',
  SERVER_ERROR_MIN_STATUS: 500,
  ABORT_ERROR_NAME: 'AbortError',
  REASONS: [
    'network_error',
    'server_error',
    'timeout',
    'script_not_loaded',
    'search_failed'
  ],
  FALLBACK_REASON_ATTRIBUTE: 'fallbackReason',
  PREDICTIVE_ENDPOINT: 'SearchRequestCollection',
  RESULTS_ENDPOINT: 'ProductSearchRequest',
  RELEWISE_FORM_SELECTOR: 'search-form.js-relewise-search-form',
  NATIVE_TEMPLATE_SELECTOR: '.js-native-search-template',
  NATIVE_HEADER_ID: 'header-searchbox',
  NATIVE_INPUT_SELECTOR: 'input[name="q"]'
} as const;

const relewiseTags = {
  TAG_PREFIX: 'relewise-',
  SEARCH_TAG: 'relewise-product-search',
  OVERLAY_TAG: 'relewise-product-search-overlay',
  OVERLAY_PRODUCT_TAG: 'relewise-product-search-overlay-product',
  PRODUCT_TILE_TAG: 'relewise-product-tile'
} as const;

const relewiseRecommendations = {
  TAGS: {
    purchased_with_product: 'relewise-purchased-with-product',
    personal_products: 'relewise-personal-products',
    products_viewed_after_viewing_product:
      'relewise-products-viewed-after-viewing-product',
    popular_products: 'relewise-popular-products'
  },
  STATE_CHANGED_EVENT: 'relewise-ui-components:recommendation-state-changed',
  REGISTRY_WINDOW_KEY: 'relewiseUIRecommendationTargetedConfigurations',
  PDP_LOCATION: 'PDP - Also bought',
  PDP_PATTERN_LOCATION: 'PDP - Viewed after',
  PDP_TARGET_PREFIX: 'pdp-also-bought',
  SOURCES: {
    RELEWISE: 'relewise',
    SHOPIFY: 'shopify',
    NONE: 'none'
  },
  PRODUCT_TYPES: {
    PATTERN: 'pattern',
    OTHER: 'other'
  },
  SECTION_TYPES: {
    RELEWISE: 'relewise-recommendations',
    SHOPIFY: 'shopify-recommendations'
  },
  VARIANTS: {
    TEST: 'test',
    CONTROL: 'control'
  },
  DEFAULT_COUNT: 8,
  HIDDEN_CLASS: 'hidden',
  ARROWS_TEMPLATE_SELECTOR: '.js-slider-arrows-template',
  READY_TIMEOUT_MS: 3000,
  RESULTS_TIMEOUT_MS: 8000,
  CARD_RENDER_TIMEOUT_MS: 1500
} as const;

const constructorBeacon = {
  UC_SERVICE_ID: 'cphN9wqOfmJOnf',
  SCRIPT_BASE_URL: 'https://cnstrc.com/js/cust/',
  STORAGE_KEY_PREFIXES: ['_constructorio_', 'ConstructorioID_']
} as const;

// Browser-injected errors we cannot fix and do not want to bill against our
// error-tracking quotas (Bugsnag and PostHog).
// Each entry should link to the ticket that justified its addition.
const suppressedErrorMessages = [
  // Instagram in-app webview / Safari autofill — https://hobbii.atlassian.net/browse/CE-339
  '_AutofillCallbackHandler'
] as const;

const usercentricsEvents = {
  UI_INITIALIZED: 'UC_UI_INITIALIZED',
  UC_EVENT: 'ucEvent',
  CONSENT_STATUS: 'consent_status'
} as const;

export {
  addToCartClickOutcomes,
  addToCartClickSources,
  addToCartFailureReasons,
  bingoVideoRecoverySources,
  breakpoints,
  campaignType,
  cartUpdateFailureReasons,
  clientCookies,
  COMMUNITY_MAX_IMAGES,
  COMMUNITY_MAX_MESSAGE_LENGTH,
  constructorBeacon,
  crossSellConfig,
  domainMismatchNudge,
  entityTypes,
  FEATURE_FLAGS_KEY,
  GIFT_COLLECTION_FETCH_WINDOW,
  LEGACY_CART_ATTRIBUTE_AOV_AWARD_SHOWN,
  LOW_STOCK_THRESHOLD,
  modalEvents,
  PATTERNS_LANDING_TEST_QA_OVERRIDE_KEY,
  patternsLandingTestVariants,
  posthogEvents,
  posthogFeatureFlags,
  postSignupDetailsModal,
  PREDICTIVE_SEARCH_DEBOUNCE_MS,
  productPageSelectors,
  queryParams,
  REFERENCE_CURRENCY,
  relewiseFallback,
  relewiseRecommendations,
  relewiseTags,
  REVIEWS_PER_PAGE,
  searchEvents,
  searchPage,
  shopifyPixelEvents,
  smoothCenterViewScrollOptions,
  suppressedErrorMessages,
  usercentricsEvents,
  userCookies,
  userDetailsFormSources
};
